import React from 'react';
import { VStack, HStack, Text, Badge, Button, Icon, Box, Heading, Link } from '@chakra-ui/react';
import { Stethoscope, Building2, Tags, Edit, Play, FileText, Pill } from 'lucide-react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import DetailsSection from '@/components/ui/DetailsSection';
import RelativeTime from '@/components/ui/RelativeTime';
import EntityTableSection, { TableColumn } from '@/components/section/EntityTableSection';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { BaseDetailsModalProps } from '@/types/DetailsModalTypes';
import { useGetConditionLookupQuery } from '../api';
import { ConditionGroupLookup, useGetConditionGroupsLookupsQuery } from '@/entities/condition-groups/api';
import { ConditionResourceLookup, useGetConditionResourcesLookupsQuery } from '@/entities/condition-resources/api';
import { createVideosColumns } from '@/entities/condition-resources/table-config/videos';
import { createResourcesColumns } from '@/entities/condition-resources/table-config/resources';
import { createPrescriptionsColumns } from '@/entities/condition-resources/table-config/prescriptions';
import ConditionGroupsModal from './ConditionGroupsModal';
import { getStatusBadgeProps } from '@/utils/lookupStatus';

interface ConditionDetailsModalProps extends BaseDetailsModalProps {
  onRefresh?: () => void;
}

const ConditionDetailsModal: React.FC<ConditionDetailsModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onRefresh,
  navigateToModal,
}) => {
  // Fetch individual entity if entityId is provided (hash modal mode)
  const { data: selectedCondition } = useGetConditionLookupQuery(entityId!, {
    skip: !entityId,
  });

  // Fetch all condition groups to resolve group_ids to group names
  const { data: allGroupsResponse } = useGetConditionGroupsLookupsQuery();
  const allGroups = allGroupsResponse?.data || [];

  const {
    data: videosResponse,
    isLoading: isLoadingVideos,
    isError: isVideosError,
  } = useGetConditionResourcesLookupsQuery(
    {
      condition_lookup_id: entityId,
      type: 'recommendation',
      per_page: 5,
      page: 1,
      sort_by: 'updated_at',
      sort_direction: 'desc',
    },
    { skip: !entityId }
  );

  const {
    data: resourcesResponse,
    isLoading: isLoadingResources,
    isError: isResourcesError,
  } = useGetConditionResourcesLookupsQuery(
    {
      condition_lookup_id: entityId,
      type: 'resource',
      per_page: 5,
      page: 1,
      sort_by: 'updated_at',
      sort_direction: 'desc',
    },
    { skip: !entityId }
  );

  const {
    data: prescriptionsResponse,
    isLoading: isLoadingPrescriptions,
    isError: isPrescriptionsError,
  } = useGetConditionResourcesLookupsQuery(
    {
      condition_lookup_id: entityId,
      type: 'prescription',
      per_page: 5,
      page: 1,
      sort_by: 'updated_at',
      sort_direction: 'desc',
    },
    { skip: !entityId }
  );

  const handleGroupsModalSuccess = () => {
    onRefresh?.();
  };

  // Get assigned groups by resolving group_ids to group objects
  const getAssignedGroups = () => {
    if (!selectedCondition?.group_ids || !allGroups.length) return [];
    return allGroups.filter(group => selectedCondition.group_ids.includes(group.id));
  };

  const withResourceIdLink = (
    columns: TableColumn<ConditionResourceLookup>[],
    basePath: string
  ): TableColumn<ConditionResourceLookup>[] => {
    return columns
      .filter((column) => column.key !== 'condition_lookup_id')
      .map((column) => {
        if (column.key !== 'id') return column;
        return {
          ...column,
          render: (resource: ConditionResourceLookup) => (
            <Link
              href={`${basePath}/#/${resource.id}/view`}
              color="brand.500"
              fontSize="sm"
              fontWeight="medium"
            >
              #{resource.id}
            </Link>
          ),
        };
      });
  };

  const videosColumns = withResourceIdLink(createVideosColumns(), '/seed-videos');
  const resourcesColumns = withResourceIdLink(createResourcesColumns(), '/seed-resources');
  const prescriptionsColumns = withResourceIdLink(createPrescriptionsColumns(), '/seed-prescriptions');

  const renderResourceTable = (
    items: ConditionResourceLookup[],
    isLoading: boolean,
    isError: boolean,
    columns: ReturnType<typeof createVideosColumns>,
    emptyTitle: string,
    emptyDescription: string,
    emptyIcon: React.ComponentType
  ) => {
    return (
      <Box>
        {isError ? (
          <Text fontSize="sm" color="red.500">Failed to load resources.</Text>
        ) : (
          <Box overflowX="auto">
            <EntityTableSection<ConditionResourceLookup>
              data={items}
              columns={columns}
              isLoading={isLoading}
              emptyState={{
                icon: emptyIcon,
                title: emptyTitle,
                description: emptyDescription,
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderContent = () => {
    if (!selectedCondition) {
      return <Text>Condition not found</Text>;
    }

    return (
      <VStack gap={6} align="stretch">
        {/* Basic Information */}
        <DetailsSection
          title="Basic Information"
          icon={Stethoscope}
          items={[
            {
              label: "ID",
              value: selectedCondition.id
            },
            {
              label: "Name",
              value: selectedCondition.name
            },
            {
              label: "Description",
              value: selectedCondition.description,
              stacked: true,
              render: () => (
                <Text textAlign="left" w="full">
                  {selectedCondition.description}
                </Text>
              )
            },
            {
              label: "Status",
              value: selectedCondition.status,
              render: () => {
                const { colorPalette, label } = getStatusBadgeProps(selectedCondition.status);
                return (
                  <Badge colorPalette={colorPalette} size="sm" variant="subtle">
                    {label}
                  </Badge>
                );
              }
            },
            {
              label: "Created",
              value: selectedCondition.created_at ? "set" : null,
              render: () => <RelativeTime date={selectedCondition.created_at} />
            },
            {
              label: "Updated",
              value: selectedCondition.updated_at ? "set" : null,
              render: () => <RelativeTime date={selectedCondition.updated_at || selectedCondition.created_at} />
            }
          ]}
        />

        {/* Related Information */}
        <DetailsSection
          title="Related Information"
          icon={Building2}
          items={[
            {
              label: "Speciality",
              value: selectedCondition.speciality?.title || 'N/A'
            },
            {
              label: "Groups",
              value: selectedCondition.group_ids?.length ? "assigned" : "none",
              render: () => {
                const assignedGroups = getAssignedGroups();
                return (
                  <VStack align="end" gap={2}>
                    {assignedGroups.length > 0 ? (
                      assignedGroups.map((group: ConditionGroupLookup) => (
                        <Badge 
                          key={group.id}
                          size="sm"
                          colorPalette="blue"
                          variant="outline"
                        >
                          {group.name}
                        </Badge>
                      ))
                    ) : (
                      <Text fontSize="sm" color="gray.500">No groups assigned</Text>
                    )}
                  </VStack>
                );
              }
            }
          ]}
        />

        <Box
          p={4}
          borderWidth={1}
          borderRadius="md"
          borderColor="gray.200"
          bg="white"
          _dark={{
            borderColor: "gray.600",
            bg: "gray.700"
          }}
        >
          <VStack align="stretch" gap={4}>
            <HStack>
              <Icon as={Tags} boxSize={4} color="brand.500" />
              <Heading size="sm" color="brand.600" _dark={{ color: "brand.300" }}>
                Related Lookup Resources
              </Heading>
            </HStack>

            <VStack align="stretch" gap={5}>
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Videos</Text>
                {renderResourceTable(
                  videosResponse?.data || [],
                  isLoadingVideos,
                  isVideosError,
                  videosColumns,
                  'No videos found',
                  'No related videos are linked to this condition.',
                  Play
                )}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Resources</Text>
                {renderResourceTable(
                  resourcesResponse?.data || [],
                  isLoadingResources,
                  isResourcesError,
                  resourcesColumns,
                  'No resources found',
                  'No related resources are linked to this condition.',
                  FileText
                )}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Prescriptions</Text>
                {renderResourceTable(
                  prescriptionsResponse?.data || [],
                  isLoadingPrescriptions,
                  isPrescriptionsError,
                  prescriptionsColumns,
                  'No prescriptions found',
                  'No related prescriptions are linked to this condition.',
                  Pill
                )}
              </Box>
            </VStack>
          </VStack>
        </Box>
      </VStack>
    );
  };

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} size="lg">
        <DrawerHeader>
          <DrawerTitle color="brand.700" _dark={{ color: "brand.300" }}>
            Condition Details {selectedCondition ? `- ${selectedCondition.name}` : ''}
          </DrawerTitle>
          <DrawerCloseTrigger />
        </DrawerHeader>
        
        <DrawerBody>
          {renderContent()}
        </DrawerBody>
        
        <DrawerFooter>
          <HStack justify="space-between" w="full">
            <HStack>
              {entityId && navigateToModal && (
                <Button 
                  variant="outline" 
                  onClick={() => navigateToModal(entityId, 'edit')}
                >
                  <Icon as={Edit} boxSize={4} mr={2} />
                  Edit
                </Button>
              )}
              {selectedCondition && (
                <Button 
                  variant="outline" 
                  onClick={() => window.location.hash = `#/${selectedCondition.id}/groups`}
                >
                  <Tags size={16} style={{ marginRight: 8 }} />
                  Manage Groups
                </Button>
              )}
            </HStack>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </HStack>
        </DrawerFooter>
      </Drawer>

      {/* Condition Groups Modal */}
      <ConditionGroupsModal onSuccess={handleGroupsModalSuccess} />
    </>
  );
};

// Export wrapped version for hash modal (details modal: view action)
export default withHashDrawer(ConditionDetailsModal, {
  action: 'view' // Default view action
}); 
